/**
 * TrainingCaptureService — captures audio + facial blendshapes + head pose
 * during voice-agent interactions and uploads ≤30s chunks to the
 * `training-ingest` Edge Function.
 *
 * Mirrors the protocol of services/training/app.py in
 * linkbox-wardrobe-platform. Powers AVATAR_FINISHING_PLAN_2026.md
 * Stages 10 (emotion) + 13 (gesture/head-pose) AND the broader
 * personal-AI mission ("agents learn who you and your circle are").
 *
 * Integration with the existing voice stack:
 *   - LinkLiveSession (src/lib/voice/link-live-session.ts) calls
 *     `trainingCapture.startSession()` on `startSession()`.
 *   - On wake-word detection, `trainingCapture.startCapture()`.
 *   - On session end, `trainingCapture.stopCapture()` flushes any
 *     trailing chunk, then `endSession()`.
 *
 * Camera capture is opt-in. If the user has not granted camera consent,
 * only audio is captured (Edge Function enforces this server-side too).
 */
import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const CHUNK_SECONDS = 30;
const AUDIO_RATE = 16_000;

export interface ConsentGrants {
  audio: boolean;
  camera: boolean;
  training: boolean;
  version: string;
  bipaAck?: boolean;
}

export interface SessionMeta {
  agentId?: string;
  context?: string;     // "wake_word"|"voice_query"|"video_call"|...
  lang?: string;        // BCP-47
}

export interface TrainingCaptureOptions {
  supabase: SupabaseClient;
  householdId: string;
  memberId: string;
  getConsent: () => ConsentGrants;
}

interface FaceFrame { t: number; bs: number[]; }
interface PoseFrame { t: number; rot: number[]; pos: number[]; }

export class TrainingCaptureService {
  private supabase: SupabaseClient;
  private householdId: string;
  private memberId: string;
  private getConsent: () => ConsentGrants;
  private platform = "web";

  private sessionId: string | null = null;
  private chunkSeq = 0;
  private audioBufs: Float32Array[] = [];
  private faceBuf: FaceFrame[] = [];
  private poseBuf: PoseFrame[] = [];

  private audioCtx: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private processor: ScriptProcessorNode | null = null;
  private camStream: MediaStream | null = null;
  private videoEl: HTMLVideoElement | null = null;
  private faceLandmarker: any = null;
  private flushTimer: number | null = null;
  private faceLoopActive = false;

  constructor(opts: TrainingCaptureOptions) {
    this.supabase = opts.supabase;
    this.householdId = opts.householdId;
    this.memberId = opts.memberId;
    this.getConsent = opts.getConsent;
  }

  /**
   * Swap the active speaker mid-session. Subsequent chunks upload tagged
   * with the new memberId. Used by LinkLiveSession after a wake-word
   * identify-speaker call resolves who's actually talking.
   *
   * Does NOT re-tag chunks already in flight — those keep the previous
   * member_id (server-side diarize handles late corrections).
   */
  setMember(memberId: string): void {
    if (memberId && memberId !== this.memberId) {
      this.memberId = memberId;
    }
  }

  /** Read-only access to the current speaker for logging / UI. */
  get currentMemberId(): string {
    return this.memberId;
  }

  // ── Session lifecycle ────────────────────────────────────────────────
  async startSession(meta: SessionMeta = {}): Promise<string> {
    if (this.sessionId) return this.sessionId;
    const { data, error } = await this.supabase
      .from("training_sessions")
      .insert({
        household_id: this.householdId,
        platform: this.platform,
        agent_id: meta.agentId ?? null,
        context: meta.context ?? null,
        lang: meta.lang ?? null,
      })
      .select("id")
      .single();
    if (error) throw new Error(`startSession: ${error.message}`);
    this.sessionId = data.id;
    this.chunkSeq = 0;
    return data.id;
  }

  async endSession(): Promise<void> {
    if (!this.sessionId) return;
    await this.supabase
      .from("training_sessions")
      .update({ ended_at: new Date().toISOString() })
      .eq("id", this.sessionId);
    this.sessionId = null;
  }

  // ── Capture lifecycle ────────────────────────────────────────────────
  async startCapture(): Promise<void> {
    const c = this.getConsent();
    if (!c.training) return;                // no-op if user opted out
    if (!c.audio && !c.camera) return;
    if (!this.sessionId) throw new Error("call startSession() first");

    if (c.audio && !this.audioCtx) {
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // AudioContext.sampleRate is best-effort; some browsers ignore the
      // hint. We resample server-side if needed.
      this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: AUDIO_RATE,
      });
      const src = this.audioCtx.createMediaStreamSource(this.micStream);
      // ScriptProcessor is deprecated but the only widely-shipping way
      // to grab raw PCM in browser. AudioWorklet is the modern path —
      // swap when iOS Safari catches up.
      this.processor = this.audioCtx.createScriptProcessor(1024, 1, 1);
      this.processor.onaudioprocess = (ev) => {
        const f32 = ev.inputBuffer.getChannelData(0);
        this.audioBufs.push(new Float32Array(f32));
      };
      src.connect(this.processor);
      this.processor.connect(this.audioCtx.destination);
    }

    if (c.camera && !this.camStream) {
      this.camStream = await navigator.mediaDevices.getUserMedia({ video: true });
      this.videoEl = document.createElement("video");
      this.videoEl.srcObject = this.camStream;
      this.videoEl.muted = true;
      await this.videoEl.play();
      await this._loadMediaPipe();
      this.faceLoopActive = true;
      this._startFaceLoop();
    }

    if (this.flushTimer === null) {
      this.flushTimer = window.setInterval(
        () => this._flushChunk().catch(console.warn),
        CHUNK_SECONDS * 1000,
      );
    }
  }

  async stopCapture(): Promise<void> {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.faceLoopActive = false;
    await this._flushChunk();                // last partial
    this.processor?.disconnect();
    this.processor = null;
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    this.camStream?.getTracks().forEach((t) => t.stop());
    this.camStream = null;
    if (this.audioCtx) {
      try { await this.audioCtx.close(); } catch (_) {}
      this.audioCtx = null;
    }
    this.videoEl = null;
    this.faceLandmarker = null;
  }

  // ── MediaPipe FaceLandmarker (lazy-loaded) ──────────────────────────
  private async _loadMediaPipe(): Promise<void> {
    if (this.faceLandmarker) return;
    const vision = await import(
      // @ts-ignore — runtime ESM import; Next.js handles it
      /* webpackIgnore: true */ "https://esm.sh/@mediapipe/tasks-vision@0.10.20"
    );
    const fileset = await vision.FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.20/wasm",
    );
    this.faceLandmarker = await vision.FaceLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath:
          "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task",
      },
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true,
      runningMode: "VIDEO",
      numFaces: 1,
    });
  }

  private _startFaceLoop(): void {
    const step = () => {
      if (!this.faceLoopActive || !this.faceLandmarker || !this.videoEl) return;
      const tMs = performance.now();
      try {
        const res = this.faceLandmarker.detectForVideo(this.videoEl, tMs);
        if (res.faceBlendshapes?.[0]) {
          const bs = new Array(52).fill(0);
          res.faceBlendshapes[0].categories.forEach((c: any, i: number) => {
            if (i < 52) bs[i] = c.score;
          });
          this.faceBuf.push({ t: tMs / 1000, bs });
        }
        if (res.facialTransformationMatrixes?.[0]) {
          const m: Float32Array = res.facialTransformationMatrixes[0].data;
          // 4x4 column-major. v1: identity rot, just translation.
          this.poseBuf.push({
            t: tMs / 1000,
            rot: [1, 0, 0, 0],
            pos: [m[12], m[13], m[14]],
          });
        }
      } catch (_) { /* per-frame failures swallowed */ }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  // ── Chunk flush ──────────────────────────────────────────────────────
  private async _flushChunk(): Promise<void> {
    if (this.audioBufs.length === 0 && this.faceBuf.length === 0) return;
    if (!this.sessionId) return;
    const c = this.getConsent();
    if (!c.training) {        // user revoked mid-session — drop the buffer
      this._resetBufs();
      return;
    }

    const totalSamples = this.audioBufs.reduce((s, b) => s + b.length, 0);
    const audioPcm = new Float32Array(totalSamples);
    let off = 0;
    for (const b of this.audioBufs) { audioPcm.set(b, off); off += b.length; }
    const audioWav = floatPcmToWav(audioPcm, AUDIO_RATE);
    const arkitJsonl = this.faceBuf.length
      ? this.faceBuf.map((f) => JSON.stringify(f)).join("\n") : "";
    const poseJsonl = this.poseBuf.length
      ? this.poseBuf.map((p) => JSON.stringify(p)).join("\n") : "";
    this._resetBufs();

    const form = new FormData();
    form.set("session_id", this.sessionId);
    form.set("member_id", this.memberId);
    form.set("seq", String(++this.chunkSeq));
    form.set("platform", this.platform);
    form.set("consent_version", c.version);
    form.set("intended_for_training", String(c.training));
    form.set("audio", new Blob([audioWav], { type: "audio/wav" }), "audio.wav");
    if (arkitJsonl) {
      form.set("arkit_52", new Blob([arkitJsonl], { type: "application/jsonl" }), "arkit_52.jsonl");
    }
    if (poseJsonl) {
      form.set("head_pose", new Blob([poseJsonl], { type: "application/jsonl" }), "head_pose.jsonl");
    }

    // Get fresh session token for the Edge Function call.
    const { data: { session } } = await this.supabase.auth.getSession();
    if (!session) return;
    try {
      const url = `${(this.supabase as any).supabaseUrl}/functions/v1/training-ingest`;
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: form,
      });
      if (!res.ok) {
        console.warn("[training-capture] chunk POST failed:", res.status, await res.text());
      }
    } catch (e) {
      console.warn("[training-capture] chunk POST error:", e);
    }
  }

  private _resetBufs(): void {
    this.audioBufs = [];
    this.faceBuf = [];
    this.poseBuf = [];
  }
}

// ── 16-bit PCM WAV encoder ─────────────────────────────────────────────
function floatPcmToWav(samples: Float32Array, rate: number): ArrayBuffer {
  const n = samples.length;
  const buf = new ArrayBuffer(44 + n * 2);
  const view = new DataView(buf);
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + n * 2, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}
function writeString(v: DataView, off: number, s: string): void {
  for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i));
}
